import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import React, { FC, ReactNode } from 'react'
import { BufferGeometry } from 'three'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
} from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'
import { ModelSurfaceProvider } from './util/SurfaceProvider'

/**
 * The base 3D surface for rendering Gecko scan data on. Internally, this
 * component handles everything from markup layer to heatmap rendering.
 * @param bounds          The bounds of the chunk, in component coordinates
 * @param doWrapX         Whether this chunk wraps in the x direction
 * @param doWrapY         Whether this chunk wraps in the y direction
 * @param geometry        The 3D geometry (mesh) of this object
 * @param children        Any subcomponents of this surface
 * @param groupProps      Any positional/rotational information
 * @constructor
 */
export const ModelSurface3D: FC<
  {
    // 2D dimensions
    bounds: GeckoComponentRect
    componentModel: GeckoComponentModel
    // 3D Dimensions
    geometry: BufferGeometry
    // Component data
    children?: ReactNode
  } & Surface3DGroupProps
> = (props) => {
  const { show3D } = useAssetVizInternal()
  const { componentModel, ...surface3DProps } = props

  const wrapMode = React.useMemo(() => {
    const type = componentModel.type
    if (type === GeckoComponentModelType.HorizontalTankShell)
      return show3D
        ? ComponentSurfaceWrappingMode.ComponentWrapsY
        : ComponentSurfaceWrappingMode.UnrolledComponentWrapsY
    if (type === GeckoComponentModelType.PressureVesselShell)
      return show3D
        ? ComponentSurfaceWrappingMode.ComponentWrapsX
        : ComponentSurfaceWrappingMode.UnrolledComponentWrapsX
    if (
      type === GeckoComponentModelType.LegacyCone ||
      type === GeckoComponentModelType.Cone ||
      type === GeckoComponentModelType.Dome ||
      type === GeckoComponentModelType.HemisphericalHead ||
      type === GeckoComponentModelType.TorisphericalHead ||
      type === GeckoComponentModelType.EllipsoidalHead
    )
      return ComponentSurfaceWrappingMode.ComponentWrapsX
    if (
      type === GeckoComponentModelType.VerticalTankShell ||
      type === GeckoComponentModelType.VerticalTankFoundation ||
      type === GeckoComponentModelType.PipeSection ||
      type === GeckoComponentModelType.PipeElbow ||
      type === GeckoComponentModelType.DomeWall ||
      type === GeckoComponentModelType.HoodStraight ||
      type === GeckoComponentModelType.HoodBend
    )
      return show3D
        ? ComponentSurfaceWrappingMode.ComponentWrapsX
        : ComponentSurfaceWrappingMode.UnrolledComponentWrapsX

    return ComponentSurfaceWrappingMode.ComponentNoWrap
  }, [show3D, componentModel])

  return (
    <ModelSurfaceProvider componentModel={componentModel}>
      <Surface3D
        {...surface3DProps}
        wrapMode={wrapMode}
        componentModel={componentModel}
      />
    </ModelSurfaceProvider>
  )
}
